import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Clock, Warning, Package, Truck, CheckCircle, Funnel, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  received: { label: "Received", color: "bg-zinc-100 text-zinc-700 border-zinc-200", icon: Clock },
  quoted: { label: "Quoted", color: "bg-cyan-50 text-cyan-700 border-cyan-200", icon: Clock },
  approved: { label: "Approved", color: "bg-green-50 text-green-700 border-green-200", icon: CheckCircle },
  info_needed: { label: "Info Needed", color: "bg-amber-50 text-amber-700 border-amber-200", icon: Warning },
  compounding: { label: "Compounding", color: "bg-blue-50 text-blue-700 border-blue-200", icon: Package },
  shipped: { label: "Shipped", color: "bg-violet-50 text-violet-700 border-violet-200", icon: Truck },
  delivered: { label: "Delivered", color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle },
};

export default function OrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 20 });
      if (statusFilter !== "all") params.append("status", statusFilter);
      try {
        const { data } = await axios.get(`${API}/orders?${params}`, { withCredentials: true });
        setOrders(data.orders);
        setTotalPages(data.pages);
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetch();
  }, [page, statusFilter]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="orders-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">MY ORDERS</p>
          <h1 className="font-[Chivo] text-3xl font-black tracking-tighter text-zinc-950 mt-1">Order History</h1>
        </div>
        <Link
          to="/orders/new"
          className="bg-[#0055FF] text-white font-semibold px-5 py-2.5 text-sm hover:bg-[#0044CC] transition-colors"
          data-testid="new-order-link"
        >
          New Order
        </Link>
      </div>

      <div className="mb-4">
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-48 rounded-none" data-testid="orders-status-filter">
            <Funnel size={14} className="mr-2 text-zinc-400" />
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-zinc-200" data-testid="orders-list">
        {loading ? (
          <div className="p-12 text-center text-sm text-zinc-500 font-mono">LOADING...</div>
        ) : orders.length === 0 ? (
          <div className="p-12 text-center">
            <Package size={32} className="text-zinc-300 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">No orders found</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-200">
            {orders.map(order => {
              const sc = STATUS_CONFIG[order.status] || STATUS_CONFIG.received;
              const StatusIcon = sc.icon;
              return (
                <Link
                  key={order.id}
                  to={`/orders/${order.id}`}
                  className="flex items-center justify-between p-4 hover:bg-zinc-50 transition-colors"
                  data-testid={`order-link-${order.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-zinc-400">#{order.id?.slice(0, 8)}</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono font-medium border ${sc.color}`}>
                        <StatusIcon size={12} />
                        {sc.label}
                      </span>
                      {order.has_quote_items && (
                        <span className="px-2 py-0.5 text-xs font-mono font-medium bg-amber-50 text-amber-700 border border-amber-200">QUOTE REQ.</span>
                      )}
                    </div>
                    <p className="text-sm text-zinc-800 mt-1">
                      {order.items?.length} item{order.items?.length !== 1 ? "s" : ""}{order.prescription_file_ids?.length > 0 ? ` - ${order.prescription_file_ids.length} script(s)` : ""}
                    </p>
                    {order.tracking_number && (
                      <p className="text-xs text-zinc-500 mt-0.5">Tracking: {order.tracking_number}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                    <div className="text-right">
                      <p className="text-sm font-bold text-zinc-950">${order.total?.toFixed(2)}</p>
                      <p className="text-xs text-zinc-400">{new Date(order.created_at).toLocaleDateString()}</p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        axios.get(`${API}/orders/${order.id}/reorder`, { withCredentials: true })
                          .then(res => { navigate("/orders/new", { state: { reorderData: res.data } }); })
                          .catch(() => toast.error("Failed to reorder"));
                      }}
                      className="p-2 text-zinc-400 hover:text-[#0055FF] hover:bg-blue-50 transition-colors"
                      title="Reorder"
                      data-testid={`reorder-${order.id}`}
                    >
                      <ArrowsClockwise size={16} />
                    </button>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-zinc-500">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 text-sm border border-zinc-200 hover:bg-zinc-50 disabled:opacity-50">Previous</button>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-4 py-2 text-sm border border-zinc-200 hover:bg-zinc-50 disabled:opacity-50">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
